import { useState, useEffect } from 'react';
import '../../styles/user/ForgotPassword.css';
import axios from 'axios';
import { getRecaptchaToken, loadRecaptcha } from '../../utils/recaptcha';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    // Pre-load reCAPTCHA script when component mounts
    if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
      loadRecaptcha().catch(err => {
        console.warn('reCAPTCHA pre-load failed:', err);
      });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken('password_reset');
      
      if (!recaptchaToken && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
        setError('Kunne ikke verifisere. Vennligst prøv igjen.');
        return;
      }

      // Include honeypot fields (should be empty - bots will fill these)
      const submitData = {
        email,
        recaptchaToken,
        website: '', // Honeypot field
        email_confirm: '', // Honeypot field
        _submission_time: Date.now().toString()
      };
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, submitData, {
        withCredentials: true
      });
      const data = response.data;
      if (data.success) {
        setMessage('Instruksjoner for å nullstille passord er sendt til din e-post');
        setError('');
        setShowResendVerification(false);
      } else {
        const errorMsg = data.message || 'Kunne ikke behandle forespørselen';
        setError(errorMsg);
        setMessage('');
        // Check if error is about unverified email
        if (errorMsg.includes('ikke verifisert') || errorMsg.includes('verifiser e-postadressen')) {
          setShowResendVerification(true);
        } else {
          setShowResendVerification(false);
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Kunne ikke behandle forespørselen';
      setError(errorMsg);
      setMessage('');
      // Check if error is about unverified email
      if (errorMsg.includes('ikke verifisert') || errorMsg.includes('verifiser e-postadressen')) {
        setShowResendVerification(true);
      } else {
        setShowResendVerification(false);
      }
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Vennligst skriv inn e-postadressen din først');
      return;
    }

    setResendingVerification(true);
    setResendMessage('');
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification`,
        { email }
      );

      if (response.data.success) {
        setResendMessage('Verifiserings-e-post er sendt. Sjekk innboksen din.');
        setShowResendVerification(false);
      } else {
        setError(response.data.message || 'Kunne ikke sende verifiserings-e-post');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Kunne ikke sende verifiserings-e-post');
    } finally {
      setResendingVerification(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <form onSubmit={handleSubmit} className="forgot-password-form">
        <h2>Glemt passord</h2>
        {message && <div className="success-message">{message}</div>}
        {resendMessage && <div className="success-message">{resendMessage}</div>}
        {error && <div className="error-message">{error}</div>}
        {showResendVerification && (
          <div className="verification-prompt">
            <p>E-postadressen din er ikke verifisert. Vennligst verifiser e-postadressen først.</p>
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={resendingVerification}
              className="resend-verification-btn"
            >
              {resendingVerification ? 'Sender...' : 'Send verifiserings-e-post på nytt'}
            </button>
          </div>
        )}
        <input
          type="email"
          placeholder="Epost"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {/* Honeypot fields - hidden, should be empty */}
        <input
          type="text"
          name="website"
          style={{display: 'none'}}
          tabIndex="-1"
          autoComplete="off"
          value=""
          readOnly
        />
        <input
          type="text"
          name="email_confirm"
          style={{display: 'none'}}
          tabIndex="-1"
          autoComplete="off"
          value=""
          readOnly
        />
        <button type="submit">Nullstill passord</button>
        {/* reCAPTCHA v3 badge - required by Google */}
        {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
          <div className="recaptcha-notice">
            <small>
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              {' '}and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              {' '}apply.
            </small>
          </div>
        )}
      </form>
    </div>
  );
};

export default ForgotPassword; 